import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { NavLink } from 'react-router';
import { Menu, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { focusUtils } from '../design-system/utils/a11y';
import type { TopNavItem } from './UniversalTopNav';

/**
 * UniversalMobileNav — primary navigation below the `md` breakpoint.
 *
 * WHY THIS EXISTS: `UniversalTopNav` has always had an optional `mobileMenu`
 * slot, and NOT ONE of the three apps ever passed it — so the portal had no
 * mobile navigation at all. A grep for responsive utilities across the whole
 * @afrilink/ui package returned three hits, two of which were table cells.
 *
 * An optional slot that every consumer forgets is a design-system defect, not
 * three implementation bugs (ADR shared-component doctrine, R2). So
 * UniversalTopNav now renders THIS by default whenever it has items and no
 * override was supplied — a new app cannot ship without mobile nav.
 *
 * Takes the same `TopNavItem[]` as the desktop bar, so the two can never drift.
 *
 * Not built on `Modal`: that is a centred, width-capped dialog. An edge-anchored
 * full-height sheet is a different shape, and contorting Modal into one would be
 * the over-generalisation R5 warns against. It uses the same Radix primitive and
 * matches Modal's overlay/animation conventions so it reads as one system.
 */

export interface UniversalMobileNavProps {
  items: TopNavItem[];
  /** Optional heading inside the sheet. Defaults to "Menu". */
  title?: string;
  /** Rendered under the nav list — e.g. a sign-out control. */
  footer?: React.ReactNode;
  className?: string;
}

export function UniversalMobileNav({
  items,
  title = 'Menu',
  footer,
  className,
}: UniversalMobileNavProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger
        aria-label="Open main menu"
        className={cn(
          // 44px target (WCAG 2.5.8). Sits inside the dark top bar.
          'inline-flex size-11 items-center justify-center rounded-sm text-white',
          'hover:bg-white/10',
          focusUtils.ring,
          className
        )}
      >
        <Menu className="size-5" aria-hidden />
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        {/* NO animate-in / slide-in-from-* classes here, deliberately.
            `tailwindcss-animate` is installed in the MARKETING workspace only —
            not in portal or packages/ui — so those utilities do not exist in
            this bundle. Modal.tsx and UserMenu.tsx already carry them and they
            have been silently inert all along. Rather than add more dead
            classes, this renders without a transition: correct and honest.
            If animation is wanted, install the plugin (Tailwind 4 successor:
            tw-animate-css) for the portal FIRST, then add it in one pass across
            all three components. */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-40 bg-navy-primary/70 backdrop-blur-sm"
        />
        <DialogPrimitive.Content
          className={cn(
            // Above the sticky top bar (z-30).
            'fixed inset-y-0 left-0 z-50 flex w-[280px] max-w-[85vw] flex-col',
            'border-r border-border bg-card text-card-foreground shadow-modal outline-none'
          )}
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
            <DialogPrimitive.Title className="text-sm font-semibold text-foreground">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close menu"
              className={cn(
                'inline-flex size-9 items-center justify-center rounded-sm',
                'text-muted-foreground hover:bg-muted hover:text-foreground',
                focusUtils.ring
              )}
            >
              <X className="size-4" aria-hidden />
            </DialogPrimitive.Close>
          </header>

          <nav aria-label="Primary" className="min-h-0 flex-1 overflow-y-auto p-2">
            <ul className="space-y-1">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      // Close on navigate — otherwise the sheet stays open over
                      // the page the user just asked for.
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          // min-h-11 = 44px row, full-width tap area.
                          'flex min-h-11 items-center gap-3 rounded-sm px-3 text-sm transition-colors',
                          isActive
                            ? 'bg-primary/10 font-medium text-primary'
                            : 'text-foreground hover:bg-muted',
                          focusUtils.ring
                        )
                      }
                    >
                      {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge !== undefined && (
                        <span className="rounded-pill bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {footer && (
            <div className="shrink-0 border-t border-border p-2">{footer}</div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
